import { faEllipsisV, faInbox, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { format, isSameDay } from 'date-fns';
import { uniqBy } from 'lodash';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import Popup from 'reactjs-popup';
import { useBooth } from '../../../../hooks/useBooth';
import { useEventAccess } from '../../../../hooks/useEventAccess';
import { usePresentationBooking } from '../../../../hooks/usePresentationBooking';
import { Appointment, AttendeeMessagePopulated, ContactShare, MessagingAttendee, PresentationTicket, UserMatch } from '../../../../interfaces/interfaces';
import { fetchAndSetFavoriteSlots, setPostRegistrationAction } from '../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../services/helper';
import { getOwnAppointments, getOwnShares, unbookAppointment, unfollowSlot } from '../../../../services/services';
import { isTokenSet } from '../../../../utils/auth';
import { getRedirectParam } from '../../../../utils/functions';
import VyButton from '../../../common/VyButton';
import { VyConfirm } from '../../../common/VyConfirm';
import AttendeeAvatar from '../VystemLight/AttendeeAvatar';
import { AppointmentCard } from './appointment/AppointmentCard';
import { MessagingVcard } from './messaging/vcard/MessagingVcard';
import Entry from './UIComponents/AgendaUiComponent/Entry';

interface EventHomeProps {
  textColor?: string;
  title?: string | null;
  bannerColor?: string;
  emptyMessage?: string;
  hideSideTexts?: boolean;
  titleColor?: string;
  showDefaultTexts?: boolean;
  showPresentationTickets?: boolean;
}

const EventHome = ({ title, emptyMessage, bannerColor, titleColor, showDefaultTexts = true, hideSideTexts, showPresentationTickets }: EventHomeProps) => {
  const [shares, setShares] = useState<ContactShare[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuContent, setMenuContent] = useState('presentation');
  const [appointmentModal, setAppointmentModal] = useState<string>();
  const registration = useSelector(({ common }) => common.eventRegistration);
  const [selectedVCard, setSelectedVCard] = useState<AttendeeMessagePopulated>();

  const { t } = useTranslation(['eventhome', 'common']);
  const dispatch = useDispatch();

  const { getPresentationSlot } = usePresentationBooking();
  const { loadUserData } = useEventAccess();
  const { cancelPresentationTicket } = usePresentationBooking();
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event);
  const favoriteSlots = useSelector((state) => state.common.favoriteSlots);
  const favoriteAgendaEntries = useSelector((state) => state.common.favoriteAgendaEntries);
  const favouriteBooths = useSelector(({ common }) => common.favouriteBooths);
  const presentationTickets = useSelector((state) => state.common.presentationTickets);

  const slots = useSelector(({ common }) => common.slots);

  const matches = useSelector(({ common }) => common.matchHistory);

  const userMatches = matches?.map((m) => getUserMatchPartner(m)).filter((m) => m.own.hasHighlightedMatch) ?? [];
  const messagingContactshares = useSelector(({ common }) => common.messagingContactshares);

  //@ts-ignore
  const sortedPresentationTickets: PresentationTicket[] = presentationTickets?.toSorted((a, b) => {
    const slotA = slots.find((slot) => slot._id === a.slot);
    const slotB = slots.find((slot) => slot._id === b.slot);
    return new Date(slotA?.datetime_start!).getTime() - new Date(slotB?.datetime_start!).getTime();
  });

  // only own contactshare and no duplicates
  const filteredMessagingContactshares = uniqBy(
    messagingContactshares.filter((message) => message.recepient._id === user?._id),
    'recepient._id'
  );

  const router = useRouter();

  const { openBooth } = useBooth();

  const [, setPresAmount] = useState(0);

  async function fetchShares() {
    const shares = await getOwnShares(event?._id);
    setShares(shares);
  }

  async function fetchAppointments() {
    const appointments = await getOwnAppointments(event?._id);
    setAppointments(appointments);
  }

  const handleClose = () => {
    setAnchorEl(null);
  };

  function getUserMatchPartner(match: UserMatch) {
    if (match.participantA.registration._id === registration?._id) return { _id: match._id, own: match.participantA, partner: match.participantB };
    else return { _id: match._id, own: match.participantB, partner: match.participantA };
  }

  function joinRoom(roomId: string) {
    if (event?.settings?.presentations?.waitingRoomActive) {
      return router.push(`/videochat/waitingroom/${roomId}`);
    } else if (event?.videomodes?.presentation?.version === 'v2') {
      window.location.href = `${process.env.baseURL}/video/${roomId}?${getRedirectParam()}`;
    } else {
      window.location.href = `${process.env.baseURL}/videochat/${roomId}?${getRedirectParam()}`;
    }
  }

  useEffect(() => {
    if (user && event && loading && isTokenSet()) {
      fetchShares();
      fetchAppointments();
      setPresAmount(6);
      setLoading(false);
    }
  }, [user, event]);

  const sortedFavouriteBooths = favouriteBooths
    .slice()
    .filter((favorite) => favorite?.booth?.settings?.showLikeInEventHome?.value)
    .sort((a, b) => new Date(a.datetime_created).getDate() - new Date(b.datetime_created).getDate());

  const isMultiDay = !isSameDay(new Date(event?.datetime_start ?? ''), new Date(event?.datetime_end ?? ''));
  return (
    <div className="flex overflow-hidden overscroll-none w-full h-full">
      <div className="flex flex-col pb-8 w-full">
        <div className="grid gap-8">
          {isTokenSet() ? (
            <>
              {event?.settings?.enablePresentationFavorite && favoriteSlots.length > 0 && (
                <div className="grid gap-3">
                  <p className="flex justify-center text-lg">{t('eventhome:here-find')}</p>
                  {favoriteSlots.map((favorite) => {
                    const showJoinButton = favorite.slot.meetingroom.presentationSlotType !== 'on-site';
                    return (
                      <div
                        key={`favorite-${favorite._id}`}
                        className={`flex gap-3 ${showJoinButton ? 'flex-col items-start justify-between @md:flex-row @md:items-center' : 'justify-between'}`}>
                        <div className="flex gap-3 items-center break-normal">
                          <Avatar
                            src={createDisplayLink(favorite?.slot?.meetingroom?.image, false)}
                            style={{ border: '1px black solid', width: '3em', height: '3em' }}>
                            {favorite?.slot?.meetingroom?.contactperson?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <div className="grid justify-center">
                            <h5 className="font-bold">{favorite.slot.meetingroom.name}</h5>
                            <p>{favorite.slot.meetingroom.contactperson}</p>
                            <p className="text-sm" style={{ fontWeight: 300 }}>
                              {t('eventhome:from')} {`${format(new Date(favorite.slot.datetime_start), 'HH:mm')} `}
                              {t('eventhome:to')} {` ${format(new Date(favorite.slot.datetime_end), 'HH:mm')}`}
                            </p>
                          </div>
                        </div>
                        <div className={`flex gap-2 items-center ${showJoinButton ? 'w-full @md:w-auto' : ''}`}>
                          {showJoinButton && (
                            <VyButton
                              className="w-full @md:w-auto"
                              event={event}
                              disabled={favorite.slot.status === 'old'}
                              onClick={() => joinRoom(favorite.slot.meetingroom._id)}>
                              {t('eventhome:join-now')}
                            </VyButton>
                          )}
                          <IconButton
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                              setMenuContent('eventhome:presentation');
                              setAnchorEl(event.currentTarget);
                            }}>
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </IconButton>
                          <Menu anchorEl={anchorEl} open={Boolean(anchorEl) && menuContent === 'presentation'} onClose={handleClose}>
                            <MenuItem
                              onClick={async () => {
                                handleClose();
                                event &&
                                  unfollowSlot(favorite.slot._id, event?._id).then(() => {
                                    dispatch(fetchAndSetFavoriteSlots(event?._id));
                                  });
                              }}>
                              {t('eventhome:common:delete')}
                            </MenuItem>
                          </Menu>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {appointments.length > 0 && (
                <div className="grid gap-3">
                  <p className="flex justify-center text-lg">{t('eventhome:appointment-text')}</p>
                  {appointments
                    .sort((a, b) => new Date(a.datetime_start).getDate() - new Date(b.datetime_end).getDate())
                    .map((appointment) => {
                      return (
                        <div key={`appointment-${appointment._id}`} className="flex gap-3 flex-col items-start justify-between @md:flex-row @md:items-center">
                          <div className="flex gap-3 items-center break-normal">
                            <Avatar
                              src={createDisplayLink(appointment.meetingroom?.image, undefined, undefined, undefined, undefined, undefined, true)}
                              style={{ border: '1px black solid', width: '3em', height: '3em' }}>
                              {appointment.meetingroom?.contactperson?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <div className="grid justify-center">
                              <h5 className="font-bold">{appointment.meetingroom?.contactperson}</h5>
                              <p>{appointment.meetingroom?.contactposition}</p>
                              <p className="text-sm">
                                {t('eventhome:from')} {`${format(new Date(appointment.datetime_start), 'HH:mm')} `}
                                {t('eventhome:to')}
                                {` ${format(new Date(appointment.datetime_end), 'HH:mm')}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 items-center w-full @md:w-auto">
                            <VyButton onClick={() => setAppointmentModal(appointment._id)} className="w-full @md:w-auto" event={event}>
                              {t('eventhome:open')}
                            </VyButton>

                            <IconButton
                              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                setMenuContent('eventhome:appointment');
                                setAnchorEl(event.currentTarget);
                              }}>
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </IconButton>
                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl) && menuContent == 'appointment'} onClose={handleClose}>
                              {/* {event && <BoothPopup booth={appointment.booth} event={event} trigger={<MenuItem>{t('eventhome:show-booth')}</MenuItem>} />} */}
                              <MenuItem
                                onClick={async () => {
                                  handleClose();
                                  await unbookAppointment(appointment._id);
                                  await fetchAppointments();
                                }}>
                                Stornieren
                              </MenuItem>
                            </Menu>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
              {shares.length > 0 && (
                <div className="grid gap-3">
                  <p className="flex justify-center text-lg">{t('eventhome:contact-text')}</p>
                  {shares.map((share) => {
                    return (
                      <div key={`share-${share._id}`} className="flex flex-col gap-3 justify-between items-start">
                        <div className="flex gap-3 items-center break-normal">
                          <Avatar
                            src={createDisplayLink(
                              share?.meetingroomid?.booth?.logo || share.boothid?.logo,
                              undefined,
                              undefined,
                              undefined,
                              undefined,
                              undefined,
                              true
                            )}
                            style={{ border: '1px black solid', width: '3em', height: '3em' }}>
                            {share.meetingroomid?.booth?.name || share.boothid?.name}
                          </Avatar>
                          <div className="grid justify-center">
                            <h5 className="font-bold">{share.meetingroomid?.booth?.name || share.boothid?.name}</h5>
                            <p>{share.meetingroomid?.contactperson}</p>
                          </div>
                        </div>
                        <div className="grid justify-center">
                          <p className="text-sm">{t('eventhome:your-message')}:</p>
                          <p>{share.message}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {presentationTickets.length > 0 && (
                <div className="grid gap-3">
                  <p className="flex justify-center text-lg">{t('eventhome:presentation-tickets-text')}</p>
                  {sortedPresentationTickets.map((ticket) => {
                    const slot = getPresentationSlot(ticket);
                    if (!slot) return null;
                    const showJoinButton = slot?.meetingroom.presentationSlotType !== 'on-site';
                    return (
                      <div
                        key={`favorite-${ticket._id}`}
                        className={`flex gap-3 ${showJoinButton ? 'flex-col items-start justify-between @md:flex-row @md:items-center' : 'justify-between'}`}>
                        <div className="flex gap-3 items-center break-normal">
                          <Avatar src={createDisplayLink(slot?.meetingroom?.image, false)} style={{ border: '1px black solid', width: '3em', height: '3em' }}>
                            {slot?.meetingroom?.contactperson?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <div className="grid justify-center">
                            <h5 className="font-bold">{slot.meetingroom.name}</h5>
                            <p>{slot.meetingroom.contactperson}</p>
                            <p className="text-sm">
                              {t('eventhome:from')} {`${format(new Date(slot.datetime_start), `${isMultiDay ? 'dd.MM.yyyy ' : ''}HH:mm`)} `}
                              {t('eventhome:to')}
                              {` ${format(new Date(slot.datetime_end), `HH:mm`)}`}
                            </p>
                          </div>
                        </div>

                        <div className={`flex gap-2 items-center ${showJoinButton ? 'w-full @md:w-auto' : ''}`}>
                          {showJoinButton && (
                            <VyButton
                              className="w-full @md:w-auto"
                              event={event}
                              disabled={slot.status === 'old'}
                              onClick={() => joinRoom(slot.meetingroom._id)}>
                              {t('eventhome:join-now')}
                            </VyButton>
                          )}
                          <VyConfirm
                            title={t('event:presentation-ticket.confirm-cancelation-title')}
                            message={t('event:presentation-ticket.confirm-cancel-description')}
                            onConfirm={async () => {
                              handleClose();
                              await cancelPresentationTicket(slot._id);
                            }}>
                            <FontAwesomeIcon icon={faTrash} />
                          </VyConfirm>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {favoriteAgendaEntries.length > 0 && (
                <div className="grid gap-3">
                  <p className="flex justify-center text-lg">{t('eventhome:agenda-entries-text')}</p>
                  {favoriteAgendaEntries.map((favorite) => {
                    return (
                      <Entry
                        entry={favorite.entry}
                        withHoverAnimation={false}
                        uiComponent={{ allowEntriesFavoriting: true, _id: favorite.entry.component }}
                        openSpeaker={() => {}}
                      />
                    );
                  })}
                </div>
              )}
              {favouriteBooths.length > 0 && (
                <div className="grid gap-3">
                  <p className="flex justify-center text-lg">{t('eventhome:booths-description')}</p>

                  {sortedFavouriteBooths.map(({ booth }) => {
                    return (
                      <div key={`booth-${booth._id}`} className="flex gap-3 justify-between items-center">
                        <div className="flex gap-3 items-center break-normal">
                          <Avatar
                            src={createDisplayLink(booth?.logo, undefined, undefined, undefined, undefined, undefined, true)}
                            style={{ border: '1px black solid', width: '3em', height: '3em' }}>
                            {booth?.name.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <p className="name" style={{ fontWeight: 800 }}>
                            {booth?.name}
                          </p>
                        </div>
                        <VyButton onClick={() => openBooth(booth?._id)} className="col-span-2 @md:col-span-1 my-2" event={event}>
                          {t('eventhome:open')}
                        </VyButton>
                      </div>
                    );
                  })}
                </div>
              )}
              {userMatches && userMatches?.length > 0 && (
                <div className="grid gap-3">
                  <p className="flex justify-center text-lg">{t('eventhome:user-matches-description')}</p>
                  {userMatches.map((userMatch) => {
                    return (
                      <div key={`match-${userMatch._id}`} className="flex gap-3 items-center break-normal">
                        <AttendeeAvatar attendee={userMatch?.partner.registration.user as MessagingAttendee} />
                        <p>{userMatch?.partner.registration.user?.name}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              {filteredMessagingContactshares.length > 0 && (
                <div className="grid gap-3">
                  <p className="flex justify-center text-lg">{t('eventhome:messaging-contact-share-text')}</p>
                  {filteredMessagingContactshares.map((message) => {
                    return (
                      <div key={`share-${message._id}`} className="flex gap-3 items-center break-normal" onClick={() => setSelectedVCard(message)}>
                        <AttendeeAvatar attendee={message.sender} />
                        <p>{message.sender.name}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              {(emptyMessage || showDefaultTexts) &&
                (favoriteSlots.length < 1 || !event?.settings?.enablePresentationFavorite) &&
                shares.length < 1 &&
                appointments.length < 1 &&
                favouriteBooths.length < 1 &&
                favoriteAgendaEntries.length < 1 &&
                userMatches.length < 1 &&
                filteredMessagingContactshares.length < 1 &&
                presentationTickets.length < 1 &&
                !loading && (
                  <div className="flex flex-col justify-center items-center">
                    <FontAwesomeIcon icon={faInbox} color="#8f8f8f" style={{ height: '5em', width: '5em' }} />
                    <span className="mt-8 w-1/2 text-center">{emptyMessage ? emptyMessage : t('eventhome:empty-message')}</span>
                  </div>
                )}
            </>
          ) : (
            <div className="flex flex-col justify-center items-center">
              <span className="mt-8 w-1/2 text-center">{t('eventhome:login-message')}</span>
              <VyButton event={event} onClick={() => dispatch(setPostRegistrationAction(() => loadUserData()))}>
                {t('eventhome:login')}
              </VyButton>
            </div>
          )}
        </div>
        <Popup
          lockScroll
          modal
          open={!!appointmentModal}
          onClose={() => setAppointmentModal(undefined)}
          overlayStyle={{ background: 'rgba(0, 0, 0, 0.2)' }}
          contentStyle={{
            border: 'none',
            padding: 0,
          }}>
          <AppointmentCard appointmentId={appointmentModal} />
        </Popup>
        <Popup
          lockScroll
          modal
          open={!!selectedVCard}
          onClose={() => setSelectedVCard(undefined)}
          overlayStyle={{ background: 'rgba(0, 0, 0, 0.2)' }}
          contentStyle={{
            border: 'none',
            padding: 0,
          }}>
          {selectedVCard && <MessagingVcard attendee={selectedVCard.sender} message={selectedVCard._id} isOwn={false} instantLoadDetails />}
        </Popup>
      </div>
    </div>
  );
};

export default EventHome;
